import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { AcceptQuoteDialog } from '../../components/AcceptQuoteDialog';
import { Quote, QuoteStatus, QuoteHistory, TransferRequestDto, AcceptQuoteDto } from '../../types/b2b';

const fetchQuote = async (id: string): Promise<Quote> => {
  const response = await axiosInstance.get(`/quotes/${id}`);
  return response.data;
};

const fetchQuoteHistory = async (id: string): Promise<QuoteHistory[]> => {
  const response = await axiosInstance.get(`/quotes/${id}/history`);
  return response.data;
};

const getStatusColor = (status: QuoteStatus): string => {
  switch (status) {
    case 'draft': return palette.textMuted;
    case 'sent': return palette.info;
    case 'accepted': return palette.success;
    case 'rejected': return palette.error;
    case 'expired': return palette.warning;
    case 'canceled': return palette.textMuted;
    default: return palette.textMuted;
  }
};

export const QuoteShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [acceptDialog, setAcceptDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: quote, isLoading } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => fetchQuote(id!),
    enabled: !!id,
  });

  const { data: history } = useQuery({
    queryKey: ['quote-history', id],
    queryFn: () => fetchQuoteHistory(id!),
    enabled: !!id,
  });

  const sendMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/quotes/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      queryClient.invalidateQueries({ queryKey: ['quote-history', id] });
      showMessage('Quote sent successfully', 'success');
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to send quote', 'error'),
  });

  const acceptMutation = useMutation({
    mutationFn: (data: AcceptQuoteDto) => axiosInstance.post(`/quotes/${id}/accept`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      queryClient.invalidateQueries({ queryKey: ['quote-history', id] });
      showMessage('Quote accepted successfully', 'success');
      setAcceptDialog(false);
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to accept quote', 'error'),
  });

  const handleAcceptQuote = (fulfillmentLocationId: string, transferRequests: TransferRequestDto[], notes?: string) => {
    acceptMutation.mutate({
      fulfillmentLocationId,
      transferRequests: transferRequests.length > 0 ? transferRequests : undefined,
      notes,
    });
  };

  const rejectMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/quotes/${id}/reject`, { reason: rejectReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      queryClient.invalidateQueries({ queryKey: ['quote-history', id] });
      showMessage('Quote rejected', 'success');
      setRejectDialog(false);
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to reject quote', 'error'),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/quotes/${id}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      showMessage('Quote duplicated successfully', 'success');
      navigate(`/app/quotes/show/${response.data.id}`);
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to duplicate quote', 'error'),
  });

  const handleDownloadPdf = async () => {
    try {
      const response = await axiosInstance.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quote?.quoteNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      showMessage('PDF downloaded successfully', 'success');
    } catch {
      showMessage('Failed to download PDF', 'error');
    }
  };

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' });
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('fr-DZ');

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: palette.primary }} /></Box>;
  }

  if (!quote) {
    return <Box><Typography>Quote not found</Typography></Box>;
  }

  const statusColor = getStatusColor(quote.status);

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/quotes')} sx={{ color: colors.textMuted, mb: 2, '&:hover': { color: palette.primary } }}>Back to Quotes</Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RequestQuoteIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography variant="overline" sx={{ color: palette.primary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>QUOTE</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{quote.quoteNumber}</Typography>
              <Chip label={quote.status.toUpperCase()} sx={{ backgroundColor: alpha(statusColor, 0.1), color: statusColor, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, border: `1px solid ${statusColor}` }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {quote.status === 'draft' && (
              <>
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/app/quotes/edit/${id}`)} sx={{ borderColor: colors.border, color: colors.textPrimary }}>Edit</Button>
                <Button variant="contained" startIcon={<SendIcon />} onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending} sx={{ backgroundColor: palette.info, color: '#FFFFFF' }}>{sendMutation.isPending ? 'Sending...' : 'Send'}</Button>
              </>
            )}
            {quote.status === 'sent' && (
              <>
                <Button variant="contained" startIcon={<CheckIcon />} onClick={() => setAcceptDialog(true)} sx={{ backgroundColor: palette.success, color: '#FFFFFF' }}>Accept</Button>
                <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setRejectDialog(true)} sx={{ borderColor: palette.error, color: palette.error }}>Reject</Button>
              </>
            )}
            {quote.status === 'accepted' && (
              <Button variant="contained" startIcon={<ReceiptIcon />} onClick={() => navigate(`/app/invoices/create?quoteId=${id}`)} sx={{ backgroundColor: palette.success, color: '#FFFFFF' }}>Create Invoice</Button>
            )}
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => duplicateMutation.mutate()} disabled={duplicateMutation.isPending} sx={{ borderColor: colors.border, color: colors.textPrimary }}>Duplicate</Button>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf} sx={{ borderColor: palette.error, color: palette.error }}>PDF</Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        <Box>
          {/* Customer & Dates */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted }}>CUSTOMER</Typography>
                </Box>
                <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.bold }}>{quote.customer ? `${quote.customer.firstName} ${quote.customer.lastName}` : 'N/A'}</Typography>
                {quote.customer?.email && <Typography variant="body2" sx={{ color: colors.textMuted }}>{quote.customer.email}</Typography>}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, color: palette.warning }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted }}>DATES</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>Created: {formatDate(quote.createdAt)}</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>Valid Until: {formatDate(quote.validUntil)}</Typography>
              </Box>
            </Box>
          </Card>

          {/* Line Items */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.15) }}>
            <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>LINE ITEMS</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: colors.textMuted }}>Product</TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted }}>Qty</TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted }}>Unit Price</TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted }}>Discount</TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted }}>Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quote.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell><Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>{item.productName}</Typography>{item.productSku && <Typography variant="caption" sx={{ color: colors.textMuted }}>SKU: {item.productSku}</Typography>}</TableCell>
                      <TableCell align="center" sx={{ color: colors.textPrimary }}>{item.quantity}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="center" sx={{ color: palette.error }}>{item.discount > 0 ? `${item.discount}%` : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.bold, color: palette.success }}>{formatCurrency(item.subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Stock Transfers */}
          {quote.requiresInternalTransfer && quote.plannedTransfers && quote.plannedTransfers.length > 0 && (
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.175) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: `2px solid ${colors.border}` }}>
                <LocalShippingIcon sx={{ color: palette.primary }} />
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>STOCK TRANSFERS</Typography>
                {quote.pendingTransferIds && quote.pendingTransferIds.length > 0 ? (
                  <Chip
                    label={`${quote.pendingTransferIds.length} Active Transfer${quote.pendingTransferIds.length > 1 ? 's' : ''}`}
                    size="small"
                    sx={{ ml: 'auto', backgroundColor: palette.info, color: '#FFFFFF', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label="All Transfers Complete"
                    size="small"
                    sx={{ ml: 'auto', backgroundColor: alpha(palette.success, 0.1), color: palette.success, fontWeight: 600, '& .MuiChip-icon': { color: palette.success } }}
                  />
                )}
              </Box>

              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 2 }}>
                This quote requires internal transfers to fulfill from the selected location.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(colors.surfaceElevated, 0.5) }}>
                      <TableCell sx={{ color: colors.textMuted, fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ color: colors.textMuted, fontWeight: 600 }}>Qty</TableCell>
                      <TableCell sx={{ color: colors.textMuted, fontWeight: 600 }}>From</TableCell>
                      <TableCell sx={{ color: colors.textMuted, fontWeight: 600 }}>To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quote.plannedTransfers.map((transfer: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                            {transfer.productName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={transfer.quantity}
                            size="small"
                            sx={{ backgroundColor: alpha(palette.primary, 0.1), color: palette.primary, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarehouseIcon sx={{ fontSize: 16, color: palette.primary }} />
                            <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                              {transfer.fromLocationName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StoreIcon sx={{ fontSize: 16, color: palette.accent }} />
                            <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                              {transfer.toLocationName}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {quote.pendingTransferIds && quote.pendingTransferIds.length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: alpha(palette.info, 0.05), border: `1px solid ${alpha(palette.info, 0.2)}`, borderRadius: theme.radius.xs }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1 }}>
                    <strong>Pending Transfers:</strong> {quote.pendingTransferIds.length} transfer{quote.pendingTransferIds.length > 1 ? 's' : ''} in progress
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => navigate(`/app/stock-transfers?quoteId=${id}`)}
                    sx={{ borderColor: palette.info, color: palette.info }}
                  >
                    View Transfers
                  </Button>
                </Box>
              )}
            </Card>
          )}

          {/* Notes */}
          {(quote.terms || quote.notes) && (
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, ...animations.slideUp(0.2) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 2, pb: 1, borderBottom: `2px solid ${colors.border}` }}>NOTES</Typography>
              {quote.terms && <Box sx={{ mb: 2 }}><Typography variant="overline" sx={{ color: colors.textMuted }}>Terms & Conditions</Typography><Typography variant="body2" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>{quote.terms}</Typography></Box>}
              {quote.notes && <Box><Typography variant="overline" sx={{ color: colors.textMuted }}>Notes</Typography><Typography variant="body2" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>{quote.notes}</Typography></Box>}
            </Card>
          )}
        </Box>

        <Box>
          {/* Totals */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
            <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>TOTALS</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: colors.textMuted }}>Subtotal:</Typography><Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(quote.subtotal)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: colors.textMuted }}>Tax:</Typography><Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(quote.tax)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: colors.textMuted }}>Discount:</Typography><Typography sx={{ fontFamily: typography.fontFamily.mono, color: palette.error }}>-{formatCurrency(quote.discount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: `2px solid ${colors.border}` }}><Typography sx={{ fontWeight: typography.fontWeight.bold, color: colors.textPrimary, fontSize: typography.fontSize.lg }}>TOTAL:</Typography><Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, fontSize: typography.fontSize.xl }}>{formatCurrency(quote.total)}</Typography></Box>
            </Box>
          </Card>

          {/* History */}
          {history && history.length > 0 && (
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, ...animations.slideUp(0.15) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 2, pb: 1, borderBottom: `2px solid ${colors.border}` }}>HISTORY</Typography>
              <Timeline sx={{ p: 0, m: 0 }}>
                {history.map((entry, index) => (
                  <TimelineItem key={entry.id} sx={{ '&::before': { display: 'none' } }}>
                    <TimelineSeparator>
                      <TimelineDot sx={{ backgroundColor: getStatusColor(entry.newStatus || 'draft') }} />
                      {index < history.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>{entry.action.replace('_', ' ').toUpperCase()}</Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>{formatDateTime(entry.createdAt)}</Typography>
                      {entry.notes && <Typography variant="caption" sx={{ display: 'block', color: colors.textMuted, fontStyle: 'italic' }}>{entry.notes}</Typography>}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Card>
          )}
        </Box>
      </Box>

      {/* Accept Dialog */}
      <AcceptQuoteDialog
        open={acceptDialog}
        onClose={() => setAcceptDialog(false)}
        quote={quote}
        onAccept={handleAcceptQuote}
        isAccepting={acceptMutation.isPending}
      />

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Quote</DialogTitle>
        <DialogContent>
          <TextField label="Reason (optional)" fullWidth multiline rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => rejectMutation.mutate()} disabled={rejectMutation.isPending} sx={{ backgroundColor: palette.error }}>{rejectMutation.isPending ? 'Rejecting...' : 'Reject'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
